import { useEffect, useState } from 'react';
import RichText from 'components/shared/richtext';
import EvesReviews from 'components/shared/eves-reviews';
import renderSections from 'components/sections/render';
import useIsMobile from 'hooks/utils/useIsMobile';
import useSWR from 'swr';
import useProductViewItemTracking from 'hooks/tracking/useProductViewItemTracking';
import trackKlavioProductView from 'lib/utils/trackKlavioProductView';
import RelatedProducts from 'components/sections/relatedProducts/relatedProducts';
import { useSettings } from 'hooks';
import Expander from 'components/shared/expander/expander';
import styles from './product.module.scss';
import ProductInfo from './productInfo';
import ProductGallery from './productGallery/productGallery';
import Breadcrumbs from '../../layout/breadcrumbs';
import Highlights from '../../sections/highlights';

const PageProduct = ({ data, locale, resources }) => {
  const [shopifyData, setShopifyData] = useState({});
  const isMobile = useIsMobile();
  const { trackedItemsSelectList } = useSettings();

  const {
    thumbnailImage,
    productPictures,
    shopifyData: cachedShopifyData,
    productVideos,
    additionalSections,
  } = data;

  const fetcher = async endpoint => {
    const responseRaw = await fetch(endpoint);
    const response = await responseRaw.json();
    return response;
  };

  const swrOptions = {
    refreshInterval: 60000,
  };

  const { data: updatedProductData } = useSWR(
    cachedShopifyData?.variants?.[0]?.product_id
      ? `/api/shopify/utils/getProduct?productId=${cachedShopifyData?.variants?.[0]?.product_id}&locale=${locale}`
      : null,
    fetcher,
    swrOptions
  );

  const { data: productRecommendations } = useSWR(
    cachedShopifyData?.variants?.[0]?.product_id && resources?.settings?.localesWithWebshop?.onOrOff
      ? `/api/shopify/utils/getProductRecommendations?productId=${cachedShopifyData?.variants?.[0]?.product_id}&locale=${locale}`
      : null,
    fetcher,
    swrOptions
  );

  const { ingredientsTitle, disclaimerText, directionOfUseTitle } = resources.productpage;
  const { relatedProductsDefaultTitle, reviewsTitle } = resources.secondpartproductpage;

  const otherSections = additionalSections.filter(
    section => section.type !== 'SectionRelatedProducts'
  );
  const relatedProductsSection = additionalSections.find(
    section => section.type === 'SectionRelatedProducts'
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof updatedProductData !== 'object') return;
    if (updatedProductData.status === 200) {
      setShopifyData(updatedProductData.data);

      const productSelectListObj = trackedItemsSelectList?.find(
        p => p?.productId === updatedProductData.data.productId
      );

      const { items: productSpecs } = data?.productSpecsCollection;
      const productType = productSpecs?.find(
        ({ type }) =>
          type.filterType === 'Producttype' ||
          type.filterType === 'Type de produit' ||
          type.filterType === 'Product type'
      );
      const productCategory = productSpecs?.find(
        ({ type }) =>
          type.filterType === 'Productcategorie' ||
          type.filterType === 'Catégorie de produit' ||
          type.filterType === 'Product Category'
      );
      const productSubCategory = productSpecs?.find(
        ({ type }) =>
          type.filterType === 'Subcategorie' ||
          type.filterType === 'Sous-catégorie' ||
          type.filterType === 'Sub Category'
      );

      // eslint-disable-next-line react-hooks/rules-of-hooks
      useProductViewItemTracking({
        product: {
          name: updatedProductData.data.productTitle,
          id: updatedProductData.data.productId,
          category: productType?.value,
          category2: productCategory?.value,
          category3: productSubCategory?.value,
          price: updatedProductData.data.productPrice,
          outOfStock: Number(!updatedProductData.data?.outOfStock), // negation outOfStock to convert to in_stock for tracking
          itemListName: productSelectListObj?.itemListName,
          itemIndex: productSelectListObj?.itemIndex,
          currencyCode: updatedProductData.data?.currencyCode,
        },
      });

      trackKlavioProductView({
        id: updatedProductData?.data?.productId,
        title: updatedProductData?.data?.productTitle,
        imageUrl: thumbnailImage?.url,
        price: updatedProductData?.data?.productPrice,
        compareAtPrice: updatedProductData?.data?.productCompareAtPrice,
        vendor: updatedProductData?.data?.productVendor,
        specs: data?.productSpecsCollection,
      });
    } else {
      setShopifyData({
        productSKU: cachedShopifyData?.variants[0].sku,
        productPrice: cachedShopifyData?.variants[0].price,
        currencyCode: cachedShopifyData?.variants[0].currencyCode,
        productCompareAtPrice: cachedShopifyData?.variants[0].compareAtPrice,
        productId: cachedShopifyData?.variants[0].product_id,
        outOfStock: cachedShopifyData?.tags?.includes('out-of-stock'),
        variantId: cachedShopifyData?.variants[0].id,
        isFreeItem: cachedShopifyData?.tags?.includes('free-item'),
      });
      console.error(updatedProductData.error);
    }
  }, [updatedProductData]);

  const renderProductRecommendations = () => {
    const containsProductRecommendations = productRecommendations?.data.length;
    if (containsProductRecommendations) {
      // rendering Highlights to show slider and to preserve quick add to cart and tracking
      return (
        <Highlights
          locale={locale}
          data={{
            title: relatedProductsDefaultTitle.text,
            highlights: productRecommendations.data,
            resources,
            sectionId: 'sectionProductRecommendations',
          }}
        />
      );
    }

    // no productRecommendations happens only when the Shopify product is Draft
    if (!containsProductRecommendations && relatedProductsSection) {
      return (
        <RelatedProducts
          data={relatedProductsSection}
          locale={locale}
          resources={resources}
          className={styles.relatedProductsSection}
        />
      );
    }

    return null;
  };

  return (
    <>
      <section>
        <div className={styles.productPage}>
          {isMobile && data.breadcrumbs && <Breadcrumbs locale={locale} data={data.breadcrumbs} />}
          <ProductGallery
            key={data.slug}
            thumbnail={thumbnailImage}
            productPictures={productPictures}
            productVideos={productVideos}
          />
          <div className={styles.productInfo}>
            <ProductInfo
              locale={locale}
              resources={resources}
              productData={{
                ...data,
                shopifyData,
              }}
            />
            <div className={styles.topDivider} />
            <div className={styles.productExpanders}>
              <Expander
                data={{
                  title: ingredientsTitle?.text,
                  content: data.ingredients,
                }}
              />
              <Expander
                data={{
                  title: directionOfUseTitle?.text,
                  content: data.directionOfUse,
                }}
              />
              {data.expandersCollection.items.map(expander => (
                <Expander data={expander} />
              ))}
            </div>
            <RichText className={styles.disclaimerText} text={disclaimerText.text.json} />

            <div className={styles.variantTitleAndEAN}>
              <p className={styles.singleVariant}>{data.size}</p>
              <p className={styles.eanNumber}>{data.eanNumber}</p>
            </div>
            <div className={styles.bottomDivider} />
          </div>
        </div>

        <EvesReviews
          eanNumber={cachedShopifyData?.variants?.[0]?.sku}
          locale={locale}
          title={reviewsTitle.text}
        />

        {renderSections('', locale, otherSections, undefined, resources)}

        {renderProductRecommendations()}
      </section>
    </>
  );
};

export default PageProduct;
